import { reactive } from 'vue'
import { useRoute } from 'vue-router'
import { message } from 'ant-design-vue'

import type { StarUnlockActivity } from '@/utils/useStarUnlockActivity'
import { fetchStarUnlockActivity } from '@/utils/useStarUnlockActivity'
import { openStarActivationDialog, queryStarActivationWithDedupe } from '@/utils/useStarActivation'

export interface TipOptions {
  title: string
  content: string
  confirmText?: string
  showCancel?: boolean
  onConfirm?: () => void
}

function isStarTestEnv(): boolean {
  const hostname = location.hostname
  return (
    hostname.includes('test') || hostname.includes('localhost')
  )
}

/** 构建 0 元升级宣传页地址（活动 id 兜底） */
export function getStarUnlockPromoUrl(activityId?: string | number): string {
  const id = activityId ?? 206
  return `https://pcstatic.sylapp.cn/marketing-page-code/index.html#/specialType?norefresh=1&activityId=${id}`
}

/** 客户端内嵌弹框打开宣传页 */
export function openStarUnlockPromoDialog(
  url: string,
  title = '0元升级',
  width = 390,
  height = 844
) {
  window.open(`gw://webkitdlg?width=${width};height=${height};mode=0;title=${title};allowdestroy=1;url=${url}`)
}

/** 进入 APP 九方股商城购买权益二维码 */
export function getStarPurchaseQrUrl(): string {
  return isStarTestEnv()
    ? 'https://s.techgp.cn/3rLm29'
    : 'https://s.techgp.cn/3rLm29'
}

export interface StarUnlockState {
  visible: boolean
  loading: boolean
  showBanner: boolean
  activity: StarUnlockActivity | null
  showPromo: boolean
  showPurchase: boolean
}

export function useStarTip() {
  const route = useRoute()

  const tipDialog = reactive({
    visible: false,
    title: '',
    content: '',
    confirmText: '确认',
    showCancel: false,
    onConfirm: () => { tipDialog.visible = false }
  })

  const unlock = reactive<StarUnlockState>({
    visible: false,
    loading: false,
    showBanner: false,
    activity: null,
    showPromo: false,
    showPurchase: false,
  })

  const showTip = (opts: TipOptions) => {
    tipDialog.title = opts.title
    tipDialog.content = opts.content
    tipDialog.confirmText = opts.confirmText || '确认'
    tipDialog.showCancel = opts.showCancel || false
    tipDialog.onConfirm = opts.onConfirm || (() => { tipDialog.visible = false })
    tipDialog.visible = true
  }

  const resetUnlockState = () => {
    unlock.loading = false
    unlock.showBanner = false
    unlock.activity = null
    unlock.showPromo = false
    unlock.showPurchase = false
  }

  /** 展示 0 元升级弹框：拉活动配置，决定默认提示 / 兜底提示 */
  const showUnlockDialog = async () => {
    resetUnlockState()
    unlock.visible = true
    unlock.loading = true

    const activity = await fetchStarUnlockActivity(route)
    unlock.activity = activity
    unlock.showBanner = !!activity?.image
    unlock.loading = false
  }

  /** 关闭弹框 */
  const handleUnlockClose = () => {
    unlock.visible = false
    unlock.showPromo = false
    unlock.showPurchase = false
  }

  /** 点击缩略图 image：展示 0 元升级宣传页 */
  const handleUnlockBannerClick = () => {
    if (!unlock.activity) return
    unlock.showPromo = true
  }

  /** 点击缩略图叉号：关闭缩略图，保留次数用尽提示 */
  const handleUnlockShareClose = () => {
    unlock.showBanner = false
  }

  const handleUnlockPromoClose = () => {
    unlock.showPromo = false
  }

  /** 点击「购买使用权益」：弹框内展开购买二维码 */
  const handleUnlockPurchaseClick = () => {
    unlock.showPurchase = !unlock.showPurchase
  }

  const resolveStarUserName = (userName?: string) =>
    userName ||
    (route.query.username as string) ||
    (route.query.userName as string) ||
    ''

  /** status 2/3 或 3/4：先查待激活订单，有则弹激活框，无则弹 0 元解锁 */
  const showActivationOrUnlock = async (userName?: string) => {
    const result = await queryStarActivationWithDedupe(route, resolveStarUserName(userName))
    if (!result.success) {
      message.error(result.errorMessage)
      return
    }
    if (result.hasUnactivatedOrder) {
      openStarActivationDialog()
      return
    }
    await showUnlockDialog()
  }

  /**
   * 处理订阅/保存（strategyAction）等非一键回测接口返回的 status
   * status 1: 成功 → onSuccess
   * status 2: 名称已存在
   * status 3 / 4: 先查待激活订单 → 激活弹框 / 0 元解锁
   * status 5: 文案用 toast；「立即刷新」整页刷新
   */
  const handleBacktestStatus = async (
    status: number,
    toast: string | undefined,
    onSuccess: () => void,
    userName?: string
  ) => {
    if (status === 1) {
      onSuccess()
      return
    }
    if (status === 2) {
      showTip({ title: '温馨提示', content: '名称已存在', confirmText: '确认', showCancel: false, onConfirm: () => { tipDialog.visible = false } })
      return
    }
    if (status === 3 || status === 4) {
      await showActivationOrUnlock(userName)
      return
    }
    if (status === 5) {
      showTip({
        title: '温馨提示',
        content: toast || '操作失败，请稍后重试',
        confirmText: '立即刷新',
        showCancel: false,
        onConfirm: () => {
          tipDialog.visible = false
          window.location.reload()
        }
      })
      return
    }
    showTip({ title: '温馨提示', content: toast || '操作失败，请稍后重试', confirmText: '确认', showCancel: false, onConfirm: () => { tipDialog.visible = false } })
  }

  /**
   * 一键回测（doTestCom 等）接口 status
   * 1: 成功 → onSuccess
   * status 2 / 3: 先查待激活订单 → 激活弹框 / 0 元解锁
   * status 4 / 5: toast +「立即刷新」
   */
  const handleOneClickBacktestStatus = async (
    status: number | undefined,
    toast: string | undefined,
    onSuccess: () => void,
    userName?: string
  ) => {
    if (status === 1) {
      onSuccess()
      return
    }
    if (status === 2 || status === 3) {
      await showActivationOrUnlock(userName)
      return
    }
    if (status === 4 || status === 5) {
      showTip({
        title: '温馨提示',
        content: toast || '操作失败，请稍后重试',
        confirmText: '立即刷新',
        showCancel: false,
        onConfirm: () => {
          tipDialog.visible = false
          window.location.reload()
        }
      })
      return
    }
    showTip({ title: '温馨提示', content: toast || '回测创建失败，请稍后重试', confirmText: '确认', showCancel: false, onConfirm: () => { tipDialog.visible = false } })
  }

  return {
    tipDialog,
    unlock,
    showTip,
    showUnlockDialog,
    handleUnlockClose,
    handleUnlockBannerClick,
    handleUnlockShareClose,
    handleUnlockPromoClose,
    handleUnlockPurchaseClick,
    handleBacktestStatus,
    handleOneClickBacktestStatus,
  }
}
